// src/pages/supplier/OrdersPage.jsx
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Search, ShoppingBag, AlertTriangle, TrendingUp, Clock,
  Package, Truck, CheckCircle, ChevronRight, XCircle,
} from 'lucide-react'
import { supplierOrdersApi } from '@/api/services'
import { useCurrency } from '@/components/common/CurrencySwitcher'
import { Card, CardBody, StatusBadge, PageSpinner, Empty, Pagination, Badge } from '@/components/common/ui'

const STATUS_TABS = [
  { key: '',              label: 'All',         icon: ShoppingBag },
  { key: 'confirmed',     label: 'Confirmed',   icon: CheckCircle },
  { key: 'processing',    label: 'Processing',  icon: Clock },
  { key: 'ready_to_ship', label: 'Ready',       icon: Package },
  { key: 'shipped',       label: 'Shipped',     icon: Truck },
  { key: 'delivered',     label: 'Delivered',    icon: CheckCircle },
  { key: 'cancelled',     label: 'Cancelled',   icon: XCircle },
]

// SLA thresholds in hours per status
const SLA_HOURS = { confirmed: 24, processing: 48, ready_to_ship: 24 }

function getSlaStatus(order) {
  const hours = SLA_HOURS[order.status]
  if (!hours) return null
  const updatedAt = new Date(order.updated_at)
  const deadline = new Date(updatedAt.getTime() + hours * 3600000)
  const remaining = (deadline - new Date()) / 3600000
  return {
    remaining: Math.round(remaining * 10) / 10,
    breached: remaining <= 0,
    urgent: remaining > 0 && remaining <= 6,
  }
}

export default function SupplierOrdersPage() {
  const [status, setStatus] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const { format } = useCurrency()

  const { data, isLoading } = useQuery({
    queryKey: ['supplier-orders', status, search, page],
    queryFn: () => supplierOrdersApi.list({ status: status || undefined, search: search || undefined, page }),
  })

  const orders = data?.data?.data?.orders?.data || []
  const counts = data?.data?.data?.counts || {}
  const kpis = data?.data?.data?.kpis || {}
  const lastPage = data?.data?.data?.orders?.last_page || 1
  const totalAll = Object.values(counts).reduce((s, c) => s + c, 0)

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-display font-bold flex items-center gap-2">
          <Package size={20} className="text-pink" /> Orders
        </h1>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-white border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp size={14} className="text-green-600" />
            <p className="text-[10px] font-bold text-muted uppercase tracking-wider">Revenue (Month)</p>
          </div>
          <p className="text-xl font-extrabold text-green-600">{format(kpis.revenue_this_month || 0)}</p>
          <p className="text-[10px] text-muted">{kpis.orders_this_month || 0} orders</p>
        </div>
        <div className="bg-white border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <Clock size={14} className="text-amber-600" />
            <p className="text-[10px] font-bold text-muted uppercase tracking-wider">Action Required</p>
          </div>
          <p className="text-xl font-extrabold text-amber-600">{kpis.action_required || 0}</p>
          <p className="text-[10px] text-muted">orders need attention</p>
        </div>
        <div className={`border rounded-xl p-4 ${kpis.sla_breached > 0 ? 'bg-red-50 border-red-200' : 'bg-white border-border'}`}>
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle size={14} className={kpis.sla_breached > 0 ? 'text-red-600' : 'text-gray-400'} />
            <p className="text-[10px] font-bold text-muted uppercase tracking-wider">SLA Breached</p>
          </div>
          <p className={`text-xl font-extrabold ${kpis.sla_breached > 0 ? 'text-red-600' : 'text-gray-400'}`}>{kpis.sla_breached || 0}</p>
          <p className="text-[10px] text-muted">overdue orders</p>
        </div>
        <div className="bg-white border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <Truck size={14} className="text-indigo-500" />
            <p className="text-[10px] font-bold text-muted uppercase tracking-wider">In Transit</p>
          </div>
          <p className="text-xl font-extrabold text-indigo-500">{counts.shipped || 0}</p>
          <p className="text-[10px] text-muted">shipped orders</p>
        </div>
      </div>

      {/* Status Tabs */}
      <div className="bg-white border border-border rounded-2xl p-3 mb-5">
        <div className="flex items-center gap-1 overflow-x-auto">
          {STATUS_TABS.map(t => {
            const count = t.key === '' ? totalAll : (counts[t.key] || 0)
            const Icon = t.icon
            const isActive = status === t.key
            return (
              <button key={t.key} onClick={() => { setStatus(t.key); setPage(1) }}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                  isActive ? 'bg-pink text-white' : 'text-muted hover:bg-surface'
                }`}>
                <Icon size={13} />
                {t.label}
                {count > 0 && (
                  <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-extrabold ${
                    isActive ? 'bg-white/20' : 'bg-surface'
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4 max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
        <input className="input pl-9 w-full" placeholder="Search order number…" value={search}
          onChange={e => { setSearch(e.target.value); setPage(1) }} />
      </div>

      {/* Loading */}
      {isLoading && <PageSpinner />}

      {/* Empty */}
      {!isLoading && orders.length === 0 && (
        <Empty icon="🛍️" title="No orders"
          description={status ? `No ${status.replace(/_/g, ' ')} orders found.` : 'Orders from buyers will appear here.'} />
      )}

      {/* Order Cards */}
      <div className="space-y-3">
        {orders.map(o => {
          const sla = getSlaStatus(o)
          const isShipped = ['shipped', 'out_for_delivery'].includes(o.status)
          return (
            <Link key={o.id} to={`/supplier/orders/${o.id}`} className="block">
              <Card className={`hover:shadow-card-md hover:border-pink/30 transition-all ${
                sla?.breached ? 'border-red-300 bg-red-50/30' : sla?.urgent ? 'border-amber-300 bg-amber-50/20' : ''
              }`}>
                <CardBody>
                  <div className="flex items-center gap-4">
                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      sla?.breached ? 'bg-red-100' : sla?.urgent ? 'bg-amber-100' : 'bg-pink/10'
                    }`}>
                      <Package size={18} className={
                        sla?.breached ? 'text-red-600' : sla?.urgent ? 'text-amber-600' : 'text-pink'
                      } />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-0.5">
                        <span className="font-bold text-sm text-gray-900">{o.order_number}</span>
                        <StatusBadge status={o.status} />
                        {sla?.breached && (
                          <Badge className="bg-red-100 text-red-700 text-[10px] flex items-center gap-1">
                            <AlertTriangle size={10} /> SLA Breached
                          </Badge>
                        )}
                        {sla?.urgent && !sla?.breached && (
                          <Badge className="bg-amber-100 text-amber-700 text-[10px] flex items-center gap-1">
                            <Clock size={10} /> {Math.round(sla.remaining)}h left
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted">
                        <span>{o.buyer?.business_name || o.buyer?.name}</span>
                        <span>{new Date(o.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                        <span>{o.items?.length ?? 0} item{(o.items?.length ?? 0) !== 1 ? 's' : ''}</span>
                      </div>

                      {/* Tracking inline */}
                      {isShipped && o.tracking_number && (
                        <div className="flex items-center gap-1.5 mt-1.5 text-[11px] text-indigo-600">
                          <Truck size={12} />
                          <span>{o.courier_name} — <span className="font-mono font-bold">{o.tracking_number}</span></span>
                        </div>
                      )}
                    </div>

                    {/* Amount + Action */}
                    <div className="text-right flex-shrink-0">
                      <p className="font-extrabold text-sm text-gray-900">{format(o.total_amount)}</p>
                      <span className="text-xs text-pink font-bold flex items-center gap-0.5 justify-end mt-1">
                        Manage <ChevronRight size={12} />
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Link>
          )
        })}
      </div>

      {lastPage > 1 && <div className="mt-6"><Pagination currentPage={page} lastPage={lastPage} onPageChange={setPage} /></div>}
    </div>
  )
}
